/**
 * Structure Analyzer Service
 *
 * Analyzes story text to detect narrative structure elements.
 * Uses rule-based keyword detection + position heuristics.
 *
 * Returns 3-state detection (missing | maybe | found) with confidence scores.
 */

import {
  findAllKeywords,
  extractSnippet,
  getNormalizedText,
} from '../lib/textUtils'
import {
  type StoryElement,
  type DetectionStatus,
  type StructureAnalysisResult,
  type AnalysisNote,
  type Suggestion,
  ANALYZER_VERSION,
  STORY_ELEMENTS,
  STORY_ELEMENT_INFO,
} from '../types/domain'

// =============================================================================
// Detection Rules Configuration
// =============================================================================

interface DetectionRule {
  id: string
  keywords: string[]
  /** Expected position range [min, max] as percentile (0-100) */
  positionRange: [number, number]
  /** Weight for position matching (0-1) */
  positionWeight: number
}

/**
 * Detection rules for each story element
 */
const DETECTION_RULES: Record<StoryElement, DetectionRule> = {
  problem: {
    id: 'problem_keywords',
    keywords: [
      'but',
      'however',
      'trouble',
      "couldn't",
      'problem',
      'wrong',
      'worried',
      'scared',
      'afraid',
      'stuck',
      'lost',
      'broke',
      'failed',
      'terrible',
      'horrible',
      'disaster',
      'uh oh',
      'oh no',
    ],
    positionRange: [0, 40],
    positionWeight: 0.3,
  },
  goal: {
    id: 'goal_keywords',
    keywords: [
      'wanted',
      'needed',
      'decided',
      'had to',
      'wished',
      'hoped',
      'dream',
      'must',
      'going to',
      'would find',
      'would get',
      'would save',
      'determined',
      'set out',
    ],
    positionRange: [0, 50],
    positionWeight: 0.2,
  },
  obstacle: {
    id: 'obstacle_keywords',
    keywords: [
      'blocked',
      "couldn't",
      'stopped',
      'but then',
      'unfortunately',
      'impossible',
      'too hard',
      'too big',
      'too strong',
      'no way',
      'trapped',
      'stuck',
      'failed',
      'tried but',
    ],
    positionRange: [20, 70],
    positionWeight: 0.2,
  },
  turning_point: {
    id: 'turning_point_keywords',
    keywords: [
      'suddenly',
      'all at once',
      "that's when",
      'right then',
      'realized',
      'discovered',
      'until',
      'just then',
      'in that moment',
      'everything changed',
      'had an idea',
      'noticed',
      'saw that',
      'remembered',
    ],
    positionRange: [40, 80],
    positionWeight: 0.4, // Position is primary signal for turning points
  },
  resolution: {
    id: 'resolution_keywords',
    keywords: [
      'finally',
      'in the end',
      'at last',
      'managed to',
      'succeeded',
      'solved',
      'saved',
      'fixed',
      'won',
      'escaped',
      'made it',
      'got home',
      'happy ending',
      'safe',
      'free',
    ],
    positionRange: [60, 100],
    positionWeight: 0.3,
  },
  change: {
    id: 'change_keywords',
    keywords: [
      'learned',
      'understood',
      'never again',
      'now knew',
      'from then on',
      'different',
      'changed',
      'grew',
      'wiser',
      'braver',
      'realized that',
      'knew that',
      'would always',
      'would never',
    ],
    positionRange: [70, 100],
    positionWeight: 0.3,
  },
}

// =============================================================================
// Confidence Thresholds
// =============================================================================

/** Confidence >= this value = 'found' */
const FOUND_THRESHOLD = 60

/** Confidence >= this value = 'maybe' (below = 'missing') */
const MAYBE_THRESHOLD = 30

// =============================================================================
// Main Analysis Function
// =============================================================================

/**
 * Analyze story text for structure elements
 *
 * @param text - The story text to analyze
 * @returns Analysis result with detection status, notes, and suggestions
 */
export function analyzeStructure(text: string): StructureAnalysisResult {
  const normalizedText = getNormalizedText(text)

  if (!normalizedText) {
    return createEmptyResult()
  }

  const notes: AnalysisNote[] = []
  const present: Record<StoryElement, DetectionStatus> = {} as Record<
    StoryElement,
    DetectionStatus
  >

  // Analyze each element
  for (const element of STORY_ELEMENTS) {
    const rule = DETECTION_RULES[element]
    const note = detectElement(text, normalizedText, element, rule)
    notes.push(note)
    present[element] = note.status
  }

  // Generate suggestions for missing or weak elements
  const suggestions = generateSuggestions(notes)

  return {
    version: ANALYZER_VERSION,
    present,
    notes,
    suggestions,
  }
}

// =============================================================================
// Element Detection
// =============================================================================

/**
 * Detect a single story element in text
 */
function detectElement(
  originalText: string,
  normalizedText: string,
  element: StoryElement,
  rule: DetectionRule
): AnalysisNote {
  // Find all keyword matches
  const matches = findAllKeywords(normalizedText, rule.keywords)

  if (matches.length === 0) {
    // No keywords found
    return {
      element,
      status: 'missing',
      confidence: 0,
      ruleId: rule.id,
    }
  }

  // Find the best match based on position
  const bestMatch = findBestMatch(matches, rule)

  // Calculate confidence
  const keywordConfidence = calculateKeywordConfidence(matches.length)
  const positionConfidence = calculatePositionConfidence(
    bestMatch.percentile,
    rule.positionRange
  )

  // Weighted combination
  const totalConfidence = Math.round(
    keywordConfidence * (1 - rule.positionWeight) +
      positionConfidence * rule.positionWeight
  )

  // Determine status from confidence
  const status = getStatusFromConfidence(totalConfidence)

  // Extract snippet from original text (not normalized)
  const snippet = extractSnippet(originalText, bestMatch.index, 40)

  return {
    element,
    status,
    confidence: totalConfidence,
    ruleId: rule.id,
    excerpt: snippet,
    position: bestMatch.index,
    percentile: bestMatch.percentile,
  }
}

/**
 * Find the match that best fits the expected position range
 */
function findBestMatch(
  matches: Array<{ keyword: string; index: number; percentile: number }>,
  rule: DetectionRule
): { keyword: string; index: number; percentile: number } {
  const [minPos, maxPos] = rule.positionRange

  // Find matches within the expected range
  const inRangeMatches = matches.filter(
    m => m.percentile >= minPos && m.percentile <= maxPos
  )

  if (inRangeMatches.length > 0) {
    // Return the first in-range match
    return inRangeMatches[0]
  }

  // No in-range matches; return the closest one
  return matches.reduce((best, current) => {
    const bestDist = getDistanceFromRange(best.percentile, minPos, maxPos)
    const currentDist = getDistanceFromRange(current.percentile, minPos, maxPos)
    return currentDist < bestDist ? current : best
  })
}

/**
 * Get distance from a position range
 */
function getDistanceFromRange(
  percentile: number,
  min: number,
  max: number
): number {
  if (percentile >= min && percentile <= max) return 0
  if (percentile < min) return min - percentile
  return percentile - max
}

// =============================================================================
// Confidence Calculations
// =============================================================================

/**
 * Calculate confidence from number of keyword matches
 * More matches = higher confidence, with diminishing returns
 */
function calculateKeywordConfidence(matchCount: number): number {
  if (matchCount === 0) return 0
  if (matchCount === 1) return 50
  if (matchCount === 2) return 70
  if (matchCount === 3) return 85
  return Math.min(100, 85 + matchCount * 2)
}

/**
 * Calculate confidence based on how well position matches expectation
 */
function calculatePositionConfidence(
  percentile: number,
  expectedRange: [number, number]
): number {
  const [min, max] = expectedRange

  // Perfect match if within range
  if (percentile >= min && percentile <= max) {
    return 100
  }

  // Decay based on distance from range
  const distance = getDistanceFromRange(percentile, min, max)
  const decay = Math.max(0, 100 - distance * 2)

  return decay
}

/**
 * Convert confidence score to detection status
 */
function getStatusFromConfidence(confidence: number): DetectionStatus {
  if (confidence >= FOUND_THRESHOLD) return 'found'
  if (confidence >= MAYBE_THRESHOLD) return 'maybe'
  return 'missing'
}

// =============================================================================
// Suggestion Generation
// =============================================================================

/**
 * Kid-friendly suggestion messages for each element
 */
const SUGGESTION_MESSAGES: Record<
  StoryElement,
  { missing: string; maybe: string }
> = {
  problem: {
    missing:
      "Try adding a 'something went wrong' moment early in your story. What challenge does your character face?",
    maybe:
      "I think I found a problem in your story. Could you make it even clearer what's going wrong?",
  },
  goal: {
    missing:
      'What does your character want more than anything? Add a sentence about their biggest wish or goal!',
    maybe:
      "I see a hint of what your character wants. Can you make it super clear what they're trying to do?",
  },
  obstacle: {
    missing:
      "What's stopping your character from getting what they want? Add something that gets in the way!",
    maybe:
      'There might be an obstacle here. Make it really clear why your character is having a hard time.',
  },
  turning_point: {
    missing:
      "Add a 'suddenly everything changed' moment! This is when your character realizes something important.",
    maybe:
      'I think something changes here. Try using words like "suddenly" or "that\'s when" to make it pop!',
  },
  resolution: {
    missing:
      'How does your story end? Show us how your character solves the problem or reaches their goal.',
    maybe:
      "You're getting there! Make the ending really clear - did your character succeed?",
  },
  change: {
    missing:
      'What did your character learn? Add a sentence about how they grew or what they understand now.',
    maybe:
      "I see your character might have learned something. Can you tell us exactly what's different about them now?",
  },
}

/**
 * Generate suggestions based on analysis notes
 */
function generateSuggestions(notes: AnalysisNote[]): Suggestion[] {
  const suggestions: Suggestion[] = []

  for (const note of notes) {
    if (note.status === 'found') continue

    const messages = SUGGESTION_MESSAGES[note.element]
    const message = note.status === 'missing' ? messages.missing : messages.maybe

    // Priority based on how important the element is and how weak it is
    const priority = getSuggestionPriority(note.element, note.status)

    suggestions.push({
      element: note.element,
      message,
      priority,
    })
  }

  // Sort by priority (high first)
  const priorityOrder: Record<Suggestion['priority'], number> = {
    high: 0,
    medium: 1,
    low: 2,
  }

  return suggestions.sort(
    (a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]
  )
}

/**
 * Determine suggestion priority based on element and status
 */
function getSuggestionPriority(
  element: StoryElement,
  status: DetectionStatus
): Suggestion['priority'] {
  // Core elements that are missing get high priority
  const coreElements: StoryElement[] = ['problem', 'resolution']
  if (status === 'missing' && coreElements.includes(element)) {
    return 'high'
  }

  // Missing elements get medium priority
  if (status === 'missing') {
    return 'medium'
  }

  // "Maybe" elements get low priority (they might already be there)
  return 'low'
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Create an empty result for empty/missing text
 */
function createEmptyResult(): StructureAnalysisResult {
  const present: Record<StoryElement, DetectionStatus> = {} as Record<
    StoryElement,
    DetectionStatus
  >

  for (const element of STORY_ELEMENTS) {
    present[element] = 'missing'
  }

  return {
    version: ANALYZER_VERSION,
    present,
    notes: [],
    suggestions: [
      {
        element: 'problem',
        message: 'Start writing your story! Every great story begins with a character who faces a problem.',
        priority: 'high',
      },
    ],
  }
}

// =============================================================================
// Utility Functions for UI
// =============================================================================

/**
 * Get a kid-friendly label for detection status
 */
export function getStatusLabel(status: DetectionStatus): string {
  switch (status) {
    case 'found':
      return 'Nice!'
    case 'maybe':
      return 'Almost there'
    case 'missing':
      return 'Try adding this'
  }
}

/**
 * Get element info (label, emoji, description)
 */
export function getElementInfo(element: StoryElement) {
  return STORY_ELEMENT_INFO[element]
}

/**
 * Calculate overall story score (0-100)
 * Based on how many elements are found/maybe/missing
 */
export function calculateStoryScore(
  result: StructureAnalysisResult
): number {
  let score = 0
  const elementCount = STORY_ELEMENTS.length

  for (const element of STORY_ELEMENTS) {
    const status = result.present[element]
    if (status === 'found') score += 100
    else if (status === 'maybe') score += 50
    // missing = 0
  }

  return Math.round(score / elementCount)
}

/**
 * Compare two analysis results and compute improvement
 */
export function compareAnalyses(
  before: StructureAnalysisResult,
  after: StructureAnalysisResult
): {
  beforeScore: number
  afterScore: number
  improved: StoryElement[]
  regressed: StoryElement[]
  overallImprovement: number
} {
  const beforeScore = calculateStoryScore(before)
  const afterScore = calculateStoryScore(after)

  const improved: StoryElement[] = []
  const regressed: StoryElement[] = []

  const statusValue: Record<DetectionStatus, number> = {
    missing: 0,
    maybe: 1,
    found: 2,
  }

  for (const element of STORY_ELEMENTS) {
    const beforeVal = statusValue[before.present[element]]
    const afterVal = statusValue[after.present[element]]

    if (afterVal > beforeVal) improved.push(element)
    else if (afterVal < beforeVal) regressed.push(element)
  }

  return {
    beforeScore,
    afterScore,
    improved,
    regressed,
    overallImprovement: afterScore - beforeScore,
  }
}
